import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useState,
  type ReactNode,
} from "react";
import { useLocation } from "react-router-dom";

type RefreshState = {
  onRefresh: () => void | Promise<void>;
};

type HeaderRefreshContextValue = {
  setRefresh: (state: RefreshState | null) => void;
  refreshState: RefreshState | null;
  loading: boolean;
  lastRefreshedAt: number | null;
  runRefresh: () => Promise<void>;
  /** Optional line shown to the left of Refresh (e.g. server “Last synced” from the active page). */
  statusText: string | null;
  setStatusText: (text: string | null) => void;
  /** Controls rendered immediately left of the Refresh caption + button (e.g. Vision agent trace). */
  beforeRefresh: ReactNode | null;
  setBeforeRefresh: (node: ReactNode | null) => void;
  openAgentTrace: (seedRunId: string | null) => void;
  closeAgentTrace: () => void;
  agentTraceOpen: boolean;
  agentTraceSeedId: string | null;
  agentTracePanelKey: number;
};

const HeaderRefreshContext = createContext<HeaderRefreshContextValue | null>(null);

function HeaderRefreshRouteSync({ closeAgentTrace }: { closeAgentTrace: () => void }) {
  const { pathname } = useLocation();
  useEffect(() => {
    closeAgentTrace();
  }, [pathname, closeAgentTrace]);
  return null;
}

export function HeaderRefreshProvider({ children }: { children: React.ReactNode }) {
  const [refreshState, setRefreshState] = useState<RefreshState | null>(null);
  const [loading, setLoading] = useState(false);
  const [lastRefreshedAt, setLastRefreshedAt] = useState<number | null>(null);
  const [statusText, setStatusText] = useState<string | null>(null);
  const [beforeRefresh, setBeforeRefresh] = useState<ReactNode | null>(null);
  const [agentTraceOpen, setAgentTraceOpen] = useState(false);
  const [agentTraceSeedId, setAgentTraceSeedId] = useState<string | null>(null);
  const [agentTracePanelKey, setAgentTracePanelKey] = useState(0);

  const openAgentTrace = useCallback((seedRunId: string | null) => {
    setAgentTracePanelKey((k) => k + 1);
    setAgentTraceSeedId(seedRunId);
    setAgentTraceOpen(true);
  }, []);

  const closeAgentTrace = useCallback(() => {
    setAgentTraceOpen(false);
  }, []);

  const setRefresh = useCallback((state: RefreshState | null) => {
    setRefreshState(state);
    if (state == null) {
      setStatusText(null);
      // Do not clear beforeRefresh here — it is set via setBeforeRefresh by the same or another
      // page effect. Clearing it on setRefresh(null) caused Studio Data tab switches to wipe the
      // trace button and re-run unrelated layout work. Each feature still clears via its own cleanup.
    }
  }, []);

  const runRefresh = useCallback(async () => {
    if (!refreshState?.onRefresh) return;
    setLoading(true);
    try {
      await Promise.resolve(refreshState.onRefresh());
      setLastRefreshedAt(Date.now());
    } finally {
      setLoading(false);
    }
  }, [refreshState]);

  const value: HeaderRefreshContextValue = {
    setRefresh,
    refreshState,
    loading,
    lastRefreshedAt,
    runRefresh,
    statusText,
    setStatusText,
    beforeRefresh,
    setBeforeRefresh,
    openAgentTrace,
    closeAgentTrace,
    agentTraceOpen,
    agentTraceSeedId,
    agentTracePanelKey,
  };

  return (
    <HeaderRefreshContext.Provider value={value}>
      <HeaderRefreshRouteSync closeAgentTrace={closeAgentTrace} />
      {children}
    </HeaderRefreshContext.Provider>
  );
}

export function useHeaderRefresh() {
  return useContext(HeaderRefreshContext);
}
